import {
  SEED_MARKETS,
  type ErasureRequest,
  type ErasureStatus,
  type Market,
  type User,
} from '../dataProvider/types';
import { graceEndsAt, gracePhase, type GracePhase } from './erasureState';

/**
 * The erasure queue query/aggregation module (D-erasure, Phase 7 surface 6) - the ONE place erasure
 * requests are shaped for the worklist. Same seam idiom as the DSAR / sanctions worklists: v1
 * aggregates client-side from the mock provider, every selector lives here, and the real-data
 * migration swaps this module for a server-side endpoint without the surface changing.
 *
 * The GRACE deadline is DERIVED off the injected clock (requestedAt plus the grace window, the DSAR
 * deadline shape), NOT a stored flag and NO deletion engine. `executable` is the derived gate the
 * execute action reads: an OPEN request whose grace has ELAPSED. The worklist sorts by grace urgency
 * (closest to grace-end on top, elapsed first), so an operator sees which erasures are about to
 * become executable.
 *
 * REGION: OVERSIGHT-GLOBAL with a region FACET (the DSAR / sanctions compliance posture). The queue
 * spans markets by default; the optional `regionFilter` narrows by the request's stored market by
 * exact value, NOT the switcher.
 */

export interface ErasureRow {
  id: number;
  subjectUserId: number;
  memberName: string;
  memberEmail: string;
  market: Market;
  status: ErasureStatus;
  requestedAt: string;
  /** Derived: the instant the grace window ends (requestedAt plus the window). */
  graceEndsAt: string;
  /** Derived off the injected clock for an OPEN request; null once resolved. */
  gracePhase: GracePhase | null;
  /** Derived: an open request whose grace has elapsed (the execute gate). */
  executable: boolean;
  executedAt: string | null;
  cancelledAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
}

export interface ErasureQueueView {
  /** Open work (requested), urgency-sorted: grace elapsed on top, then soonest grace-end. */
  pending: ErasureRow[];
  /** History (executed + cancelled + rejected), most recent activity first. */
  resolved: ErasureRow[];
}

export interface ErasureQueueInput {
  erasureRequests: ErasureRequest[];
  users: User[];
}

export interface ErasureQueueOptions {
  /** The injected clock the grace derivation is measured against. */
  now: number;
  /** Exact-value market facet (uk / us), or null for the global view. */
  regionFilter?: Market | null;
}

/** Soonest grace-end first (so elapsed / about-to-elapse sort to the top), then id. */
const urgencyOrder = (a: ErasureRow, b: ErasureRow): number =>
  a.graceEndsAt.localeCompare(b.graceEndsAt) || a.id - b.id;

/** Resolved is history: most recent activity first, then newest request. */
const activityOf = (r: ErasureRow): string =>
  r.executedAt ?? r.cancelledAt ?? r.rejectedAt ?? r.requestedAt;
const newestFirst = (a: ErasureRow, b: ErasureRow): number =>
  activityOf(b).localeCompare(activityOf(a)) || b.id - a.id;

export function buildErasureQueueView(
  input: ErasureQueueInput,
  opts: ErasureQueueOptions,
): ErasureQueueView {
  const usersById = new Map(input.users.map((u) => [u.id, u]));

  const rows: ErasureRow[] = input.erasureRequests
    .filter((r) => SEED_MARKETS.includes(r.market))
    .filter((r) => opts.regionFilter == null || r.market === opts.regionFilter)
    .map((r) => {
      const user = usersById.get(r.subjectUserId);
      const open = r.status === 'requested';
      const phase = open ? gracePhase(r.requestedAt, opts.now) : null;
      return {
        id: r.id,
        subjectUserId: r.subjectUserId,
        memberName: user?.name ?? '',
        memberEmail: user?.email ?? '',
        market: r.market,
        status: r.status,
        requestedAt: r.requestedAt,
        graceEndsAt: graceEndsAt(r.requestedAt),
        gracePhase: phase,
        executable: phase === 'elapsed',
        executedAt: r.executedAt ?? null,
        cancelledAt: r.cancelledAt ?? null,
        rejectedAt: r.rejectedAt ?? null,
        rejectionReason: r.rejectionReason ?? null,
      };
    });

  return {
    pending: rows.filter((r) => r.status === 'requested').sort(urgencyOrder),
    resolved: rows.filter((r) => r.status !== 'requested').sort(newestFirst),
  };
}
